/**
 * Business Landing Page
 * 업체 홍보페이지 - /biz/:slug
 */

import { BusinessGallery } from "@/components/business/BusinessGallery";
import { BusinessHoursDisplay } from "@/components/business/BusinessHoursDisplay";
import { BusinessInquiryForm } from "@/components/business/BusinessInquiryForm";
import { BusinessMap } from "@/components/business/BusinessMap";
import { BusinessProductCard } from "@/components/business/BusinessProductCard";
import { BusinessReviewsPlaceholder } from "@/components/business/BusinessReviewsPlaceholder";
import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  QrCode,
  Share2,
  ShoppingBag,
} from "lucide-react";
import { Link, useRoute } from "wouter";
import { extractSearchItems } from "@/lib/search-content";
import { unwrapPayload } from "@/lib/queryClient";

interface LandingPageData {
  business: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    address: string | null;
    suburb: string | null;
    state: string | null;
    postcode: string | null;
    geoLat: number | null;
    geoLng: number | null;
    coverImageUrl: string | null;
    kakaoChatUrl: string | null;
    businessHours: any;
    verified: boolean;
    subscriptionStatus: string;
    ownerNickname: string | null;
    ownerDisplayName: string | null;
    ownerProvider: string | null;
  };
  products: Array<{
    id: number;
    name: string;
    name_ko: string | null;
    description: string | null;
    description_ko: string | null;
    price_cents: number | null;
    currency: string;
    image_url: string | null;
    category: string | null;
    is_available: boolean;
    display_order: number;
  }>;
  reviews: any[];
}

export default function BusinessLandingPage() {
  const [match, params] = useRoute<{ slug: string }>("/biz/:slug");

  if (!match || !params) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 업체입니다</p>
            <Link href="/business-directory">
              <Button className="mt-4">목록으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const slug = params.slug;

  // Check if accessed via QR code
  const urlParams = new URLSearchParams(window.location.search);
  const fromQr = urlParams.get("from") === "qr";

  // Fetch landing page data
  const { data, isLoading, error } = useQuery<LandingPageData>({
    queryKey: ["/api/businesses/landing", slug],
    queryFn: async () => {
      // First, get business by slug to get ID
      const businessRes = await fetch(`/api/businesses?slug=${slug}`);
      if (!businessRes.ok) throw new Error("Business not found");
      const businesses = extractSearchItems<{ id: string }>(await businessRes.json());
      const business = businesses[0];

      if (!business) throw new Error("Business not found");

      // Track QR scan if accessed via QR
      if (fromQr && business.id) {
        try {
          await fetch(`/api/businesses/${business.id}/qr/track`, {
            method: "POST",
          });
        } catch (error) {
          console.error("Failed to track QR scan:", error);
          // Don't fail the page load if tracking fails
        }
      }

      // Then fetch landing page data
      const res = await fetch(`/api/businesses/${business.id}/landing`);
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error("Landing page is not enabled for this business");
        }
        throw new Error("Failed to fetch landing page");
      }
      return unwrapPayload<LandingPageData>(await res.json());
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {error instanceof Error
                ? error.message
                : "업체 정보를 불러올 수 없습니다"}
            </p>
            <Link href="/business-directory">
              <Button className="mt-4">목록으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const { business, products, reviews } = data;

  const formatPrice = (cents: number | null, currency: string = "AUD") => {
    if (!cents) return "가격 문의";
    return `$${(cents / 100).toFixed(0)} ${currency}`;
  };

  return (
    <PageContainer size="read">
      <div className="space-y-6">
        {/* Hero Section */}
        <Card>
          <div className="relative">
            {business.coverImageUrl && (
              <div className="h-64 w-full overflow-hidden rounded-t-lg">
                <img
                  src={business.coverImageUrl}
                  alt={business.name}
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-2xl font-bold">{business.name}</h1>
                    {business.verified && (
                      <Badge className="bg-green-600">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        인증됨
                      </Badge>
                    )}
                    {business.subscriptionStatus !== "free" && (
                      <Badge className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-0">
                        <span className="mr-1">⭐</span>
                        Premium
                      </Badge>
                    )}
                  </div>
                  {business.description && (
                    <p className="text-muted-foreground mb-4">
                      {business.description}
                    </p>
                  )}

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {business.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <a
                          href={`tel:${business.phone}`}
                          className="hover:underline"
                        >
                          {business.phone}
                        </a>
                      </div>
                    )}
                    {business.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <a
                          href={`mailto:${business.email}`}
                          className="hover:underline"
                        >
                          {business.email}
                        </a>
                      </div>
                    )}
                    {business.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <a
                          href={business.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          웹사이트
                        </a>
                      </div>
                    )}
                    {business.address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span>
                          {business.address}
                          {business.suburb && `, ${business.suburb}`}
                          {business.state && ` ${business.state}`}
                          {business.postcode && ` ${business.postcode}`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Business Hours */}
                  {business.businessHours && (
                    <div className="mt-4">
                      <BusinessHoursDisplay hours={business.businessHours} />
                    </div>
                  )}

                  {/* Owner Info */}
                  {business.ownerNickname && (
                    <div className="mt-4 flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">등록자:</span>
                      <span className="font-medium">
                        {business.ownerNickname}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = `${window.location.origin}/biz/${slug}`;
                      navigator.clipboard.writeText(url);
                      // TODO: Toast notification
                    }}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    공유
                  </Button>
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await fetch(
                            `/api/businesses/${business.id}/qr`
                          );
                          const svgBlob = await response.blob();
                          const url = window.URL.createObjectURL(svgBlob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${
                            business.slug || business.id
                          }-qr-code.svg`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error("Error downloading QR code:", error);
                        }
                      }}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      QR 다운로드
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        window.open(
                          `/api/businesses/${business.id}/qr`,
                          "_blank"
                        );
                      }}
                    >
                      QR 보기
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Products Section */}
        {products.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                상품/서비스
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                  <BusinessProductCard key={product.id} product={product} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gallery Section */}
        {/* Note: Gallery images would come from business.coverImageUrl or a separate images array */}
        {/* For now, we'll show gallery if there are product images */}
        {products.some((p) => p.image_url) && (
          <BusinessGallery
            images={products
              .filter((p) => p.image_url)
              .map((p) => p.image_url!)
              .concat(business.coverImageUrl ? [business.coverImageUrl] : [])}
            title="갤러리"
          />
        )}

        {/* Reviews Section */}
        <BusinessReviewsPlaceholder />

        {/* Map Section */}
        {business.geoLat && business.geoLng && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                위치
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BusinessMap
                lat={business.geoLat}
                lng={business.geoLng}
                address={business.address || ""}
                name={business.name}
              />
            </CardContent>
          </Card>
        )}

        {/* Inquiry Section */}
        {business.subscriptionStatus !== "free" && (
          <Card>
            <CardHeader>
              <CardTitle>문의하기</CardTitle>
            </CardHeader>
            <CardContent>
              <BusinessInquiryForm
                businessId={business.id}
                businessName={business.name}
                products={products}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </PageContainer>
  );
}
